package puzzlekey

import kotlin.js.Promise
import kotlin.js.json

/**
 * The Scan model in the browser (ADR-0009 §7): the same ONNX file as the JVM's [ScanModel], run by the
 * vendored ONNX Runtime Web (`vendor/onnxruntime-web-1.30.0`) on its single-threaded WASM backend.
 * `ModelGolden` checks it gives the JVM's embedding.
 */
class WebScanModel private constructor(private val ort: dynamic, private val session: InferenceSession) {
    /** The embedding of one [modelInput] tensor. */
    suspend fun embed(input: FloatArray): FloatArray {
        require(input.size == 3 * MODEL_SIZE * MODEL_SIZE) { "expected a [1, 3, $MODEL_SIZE, $MODEL_SIZE] input, got ${input.size} floats" }
        val shape = arrayOf(1, 3, MODEL_SIZE, MODEL_SIZE)
        val tensor = js("Reflect").construct(ort.Tensor, arrayOf("float32", input, shape))
        val result = session.run(json(session.inputNames[0] to tensor)).await()
        return result[session.outputNames[0]].data.unsafeCast<FloatArray>()
    }

    companion object {
        /**
         * [ort] is the runtime's namespace (the global `ort` that `ort.wasm.min.js` defines); [model] is the
         * ONNX file's URL or its bytes (a `Uint8Array`).
         */
        suspend fun load(ort: dynamic, model: Any): WebScanModel {
            // One thread: threads need cross-origin isolation (COOP/COEP headers), which the site doesn't set.
            ort.env.wasm.numThreads = 1
            val session = (ort.InferenceSession.create(model) as Promise<InferenceSession>).await()
            return WebScanModel(ort, session)
        }
    }
}

/** The part of ONNX Runtime Web's session API used here. */
external interface InferenceSession {
    val inputNames: Array<String>
    val outputNames: Array<String>
    fun run(feeds: dynamic): Promise<dynamic>
}
